import { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import loadingWalk from '@/assets/gifs/loading-walk.gif';
import { BaseDialog } from '@/components/shared/base-dialog';
import { Icon } from '@/components/shared/icon';
import { useNotification } from '@/context/notification';
import { clientKeys } from '@/data/@clients/client-keys';
import { PlanStatus } from '@/data/idp/plans/interfaces';
import { usePlanStatuses } from '@/data/idp/plans/statuses';

enum FinishPlanDialogStatus {
  Confirm = 'confirm',
  Loading = 'loading',
  Finished = 'finished',
}

interface FinishPlanDialogProps {
  id: string;
  onClose: () => void;
}

const Confirm = () => {
  return (
    <Stack gap={2}>
      <Icon name="siren-rounded" />
      <Typography variant="h2">Deseja concluir este plano?</Typography>
      <Typography>
        Conclua o plano para sinalizar que você alcançou seus objetivos e está
        pronto para encerrar esta jornada!
      </Typography>
      <Typography>
        Você pode rever planos concluídos quando quiser, na seção de Plano
        Personalizado.
      </Typography>
    </Stack>
  );
};

const Loading = () => {
  return (
    <Stack gap={2}>
      <Box
        component="img"
        src={loadingWalk}
        sx={{ height: 128, width: 128 }}
        alt=""
      />
      <Typography variant="h3">Concluindo plano...</Typography>
    </Stack>
  );
};

const Finished = () => {
  return (
    <Stack gap={2}>
      <Icon name="check-icon" />
      <Typography variant="h2">Plano concluído com sucesso!</Typography>
      <Typography>
        Parabéns por essa conquista! Agora é hora de dar início a novos desafios
        e continuar evoluindo o seu aprendizado.
      </Typography>
    </Stack>
  );
};

export const FinishPlanDialog = ({ id, onClose }: FinishPlanDialogProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [status, setStatus] = useState<FinishPlanDialogStatus>(
    FinishPlanDialogStatus.Confirm
  );
  const { mutate: finishPlan, isPending } = usePlanStatuses();
  const { toast } = useNotification();

  const handleFinishPlan = () => {
    finishPlan(
      { id, status: PlanStatus.Closed },
      {
        onSuccess: () => setStatus(FinishPlanDialogStatus.Loading),
        onError: () => {
          toast({
            content: 'Erro ao concluir o plano. Tente novamente.',
            type: 'error',
          });
        },
      }
    );
  };

  const invalidatePlanShow = () => {
    queryClient.invalidateQueries({ queryKey: [clientKeys.usePlanShow] });
    queryClient.invalidateQueries({ queryKey: [clientKeys.usePlansIndex] });
    navigate('/pdi/planos');
    onClose();
  };

  useEffect(() => {
    if (status !== FinishPlanDialogStatus.Loading) return;

    const timer = setTimeout(() => {
      setStatus(FinishPlanDialogStatus.Finished);
    }, 3000);

    return () => clearTimeout(timer);
  }, [status]);

  return (
    <BaseDialog
      onClose={onClose}
      actionsComponent={
        <>
          {status === FinishPlanDialogStatus.Confirm && (
            <>
              <Button>Cancelar</Button>
              <Button
                variant="contained"
                onClick={handleFinishPlan}
                loading={isPending}
              >
                Concluir plano
              </Button>
            </>
          )}
          {status === FinishPlanDialogStatus.Finished && (
            <Button variant="contained" onClick={invalidatePlanShow}>
              Fechar
            </Button>
          )}
        </>
      }
    >
      {status === FinishPlanDialogStatus.Confirm && <Confirm />}
      {status === FinishPlanDialogStatus.Loading && <Loading />}
      {status === FinishPlanDialogStatus.Finished && <Finished />}
    </BaseDialog>
  );
};
